import firebase from "firebase"
import { db, auth } from "../../../utils/firebase"

import { AI_FLOWS, getFlowSeedById } from "../_seeds/supplier-document-flow"

export const AI_FLOWS_COLLECTION = "ai_flows"

/**
 * reactflow decorates nodes/edges with transient view state (width, height,
 * selected, dragging, positionAbsolute, handle bounds...). None of that should
 * be persisted — strip each node/edge back to the canonical shape before a
 * write so Firestore stores a clean graph and reload is deterministic.
 *
 * Firestore also rejects `undefined`, so optional handle ids are coerced to
 * null rather than left off.
 */
const sanitiseNode = (node) => ({
	id: node.id,
	type: node.type || "agent",
	position: {
		x: Math.round(node.position?.x ?? 0),
		y: Math.round(node.position?.y ?? 0),
	},
	data: node.data || {},
})

const sanitiseEdge = (edge) => ({
	id: edge.id,
	source: edge.source,
	target: edge.target,
	sourceHandle: edge.sourceHandle ?? null,
	targetHandle: edge.targetHandle ?? null,
	data: edge.data || {},
})

const slugify = (text) =>
	(text || "")
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 40) || "flow"

const randomSuffix = () => Math.random().toString(36).slice(2, 6)

/**
 * List every flow available to the picker: the in-code seeds (e.g. the
 * supplier-document pipeline) merged with any saved `ai_flows` docs. A read
 * failure (rule not deployed / offline) is swallowed so the seeds still show;
 * the reason is handed back for an inline notice.
 */
export const listFlows = async () => {
	let saved = []
	let error = null

	try {
		const snap = await db.collection(AI_FLOWS_COLLECTION).get()
		saved = snap.docs.map((doc) => {
			const data = doc.data() || {}
			return {
				id: doc.id,
				label: data.label || data.flow_id || doc.id,
				description: data.description || "",
				persisted: true,
				updated_at: data.updated_at || null,
			}
		})
	} catch (err) {
		error = err?.message || String(err)
	}

	const seedItems = AI_FLOWS.filter((seed) => !saved.some((f) => f.id === seed.id)).map(
		(seed) => ({
			id: seed.id,
			label: seed.label,
			description: seed.description || "",
			persisted: false,
		})
	)

	const flows = [...seedItems, ...saved].sort((a, b) => a.label.localeCompare(b.label))
	return { flows, error }
}

/**
 * Pull a flow graph from Firestore. Returns `null` when the document doesn't
 * exist yet — callers seed from the in-code flow definition in that case (the
 * graph isn't written until the first Save, matching the rest of AI Tooling's
 * no-autosave convention).
 */
export const loadFlow = async (flowId) => {
	const snap = await db.collection(AI_FLOWS_COLLECTION).doc(flowId).get()
	if (!snap.exists) return null
	const data = snap.data() || {}
	return {
		nodes: Array.isArray(data.nodes) ? data.nodes : [],
		edges: Array.isArray(data.edges) ? data.edges : [],
		label: data.label || null,
		description: data.description || null,
		updated_at: data.updated_at || null,
		updated_by: data.updated_by || null,
	}
}

/**
 * Resolve the graph to render: the saved Firestore copy if it exists, else the
 * in-code seed (when there is one — freeform flows created in the UI always
 * have a saved doc). The `persisted` flag drives the "unsaved seed" state and
 * `loadError` carries a read failure so the canvas can warn without blanking.
 */
export const loadFlowOrSeed = async (flowId) => {
	const seed = getFlowSeedById(flowId)

	let saved = null
	let loadError = null
	try {
		saved = await loadFlow(flowId)
	} catch (err) {
		loadError = err?.message || String(err)
	}

	if (saved) {
		return {
			...saved,
			label: saved.label || seed?.label || flowId,
			description: saved.description || seed?.description || "",
			persisted: true,
			loadError: null,
		}
	}

	if (!seed) throw new Error(loadError || `Unknown AI flow: ${flowId}`)

	return {
		nodes: seed.nodes.map((n) => ({ ...n })),
		edges: seed.edges.map((e) => ({ ...e })),
		label: seed.label,
		description: seed.description || "",
		updated_at: null,
		updated_by: null,
		persisted: false,
		loadError,
	}
}

/**
 * Create a brand-new (empty) freeform flow. The id is a slug of the name plus a
 * short random suffix so two flows with the same name don't collide. Returns
 * the new flow's id + meta so the caller can switch to it.
 */
export const createFlow = async ({ label, description }) => {
	const id = `${slugify(label)}-${randomSuffix()}`
	const FV = firebase.firestore.FieldValue
	await db
		.collection(AI_FLOWS_COLLECTION)
		.doc(id)
		.set({
			flow_id: id,
			label: label.trim(),
			description: (description || "").trim(),
			nodes: [],
			edges: [],
			created_at: FV.serverTimestamp(),
			updated_at: FV.serverTimestamp(),
			updated_by: auth.currentUser?.uid || null,
		})
	return { id, label: label.trim(), description: (description || "").trim() }
}

export const deleteFlow = async (flowId) => {
	await db.collection(AI_FLOWS_COLLECTION).doc(flowId).delete()
}

/**
 * Persist the current graph (and its name/description when supplied). Stamps the
 * updater + a server timestamp. `merge: true` preserves `created_at` and any
 * fields not included here.
 */
export const saveFlow = async (flowId, { nodes, edges, label, description }) => {
	const payload = {
		flow_id: flowId,
		nodes: (nodes || []).map(sanitiseNode),
		edges: (edges || []).map(sanitiseEdge),
		updated_at: firebase.firestore.FieldValue.serverTimestamp(),
		updated_by: auth.currentUser?.uid || null,
	}
	if (typeof label === "string") payload.label = label
	if (typeof description === "string") payload.description = description

	await db.collection(AI_FLOWS_COLLECTION).doc(flowId).set(payload, { merge: true })
}
